import { useMemo, useRef, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Checkbox, FormControlLabel } from '@mui/material';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { useReactToPrint } from 'react-to-print';

import { HuddlePrintoutContent } from '@/modules/patientList/huddlePrintoutContent';
import { getProviderName } from '@/modules/patientList/huddlePrintoutContent/helpers';
import { OperatoriesDropdown } from '@/modules/patientList/operatoriesDropdown';
import { PrivacyButton } from '@/modules/patientList/PrivacyButton';
import { useShouldHideHuddle } from '@/modules/patientList/useShouldHideHuddle';
import type { Appointment } from '@/react-query/useAppointments';
import type { GetTaskResponse } from '@/react-query/useGetTasks';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';

import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';

export type ProviderAndAppointments = [string, Appointment[]];

const SCROLLBAR_CLASSES =
  '[&::-webkit-scrollbar]:vai-w-2 [&::-webkit-scrollbar-track]:vai-bg-gray-100 [&::-webkit-scrollbar-thumb]:vai-bg-gray-400 [&::-webkit-scrollbar-thumb]:vai-rounded';

interface Props {
  appointmentIdsToTasks: Record<string, GetTaskResponse>;
  appointments: Appointment[];
  visitSummaries?: TVisitSummary[];
  isOpen: boolean;
  onClose: () => void;
}

export const PrintDownloadDialog = ({
  appointmentIdsToTasks,
  appointments,
  visitSummaries,
  isOpen,
  onClose,
}: Props) => {
  const printDownloadRef = useRef(null);

  // state
  const [shouldGroupByProvider, setShouldGroupByProvider] = useState(false);

  // hooks
  const hideHuddle = useShouldHideHuddle();
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const handleReactToPrint = useReactToPrint({
    contentRef: printDownloadRef,
  });

  // variables
  const shouldHideGroupByProviderOption = useMemo(
    () =>
      hideHuddle ||
      appointments.every(
        (appointment) =>
          getProviderName(appointment.appointment.provider) === 'Unknown',
      ),
    [appointments, hideHuddle],
  );

  const appointmentsByProvider: ProviderAndAppointments[] = useMemo(() => {
    if (!shouldGroupByProvider || shouldHideGroupByProviderOption) {
      return [['All', appointments]];
    }
    const groups = appointments.reduce(
      (acc, appointment) => {
        const providerName = getProviderName(appointment.appointment.provider);
        if (acc[providerName]) {
          acc[providerName] = [...acc[providerName], appointment];
        } else {
          acc[providerName] = [appointment];
        }
        return acc;
      },
      {} as Record<string, Appointment[]>,
    );

    // Sort the group of appointments with an "Unknown" provider at the end
    return Object.entries(groups).sort(([providerNameA], [providerNameB]) => {
      if (providerNameA === 'Unknown') return 1;
      if (providerNameB === 'Unknown') return -1;
      return 0;
    });
  }, [appointments, shouldGroupByProvider, shouldHideGroupByProviderOption]);

  // handlers
  const handleToggleGroupByProvider = () => {
    const shouldGroupNewByProvider = !shouldGroupByProvider;
    setShouldGroupByProvider(shouldGroupNewByProvider);
    trackEvent(mixpanelEvents.clickGroupByProvider, {
      groupByProvider: shouldGroupNewByProvider,
    });
  };

  const handleClickPrint = () => {
    trackEvent(mixpanelEvents.clickDialogPrint, { source: 'HUDDLE_PRINTOUT' });
    handleReactToPrint();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="vai-max-w-screen-lg vai-h-[95vh] !vai-flex !vai-flex-col"
        data-testid="print-download-dialog"
      >
        {/* Fixed Header */}
        <DialogHeader>
          <DialogTitle className="!vai-text-2xl" data-testid="dialog-title">
            <Trans>Print Daily Dashboard</Trans>
          </DialogTitle>
        </DialogHeader>

        <div
          className="vai-flex vai-items-center vai-justify-between"
          data-testid="huddle-printout-actions"
        >
          <div className="vai-flex vai-items-center vai-gap-4">
            <PrivacyButton source="HUDDLE_PRINTOUT" />
            {!shouldHideGroupByProviderOption && (
              <FormControlLabel
                checked={shouldGroupByProvider}
                control={<Checkbox />}
                label={t`Group By Provider`}
                onChange={handleToggleGroupByProvider}
              />
            )}
            {!hideHuddle && <OperatoriesDropdown source="HUDDLE_PRINTOUT" />}
          </div>
        </div>
        <p className="vai-typography-small vai-text-muted-foreground">
          <Trans>Note: Completed treatments can be viewed in VideaAI</Trans>
        </p>

        {/* Scrollable Content Area */}
        <div className="vai-flex-1 vai-border vai-rounded vai-overflow-hidden">
          <div
            className={`vai-h-full vai-overflow-y-auto ${SCROLLBAR_CLASSES}`}
          >
            <div ref={printDownloadRef} className="vai-p-4">
              {appointments.length ? (
                <HuddlePrintoutContent
                  appointmentIdsToTasks={appointmentIdsToTasks}
                  appointments={appointments}
                  appointmentsByProvider={appointmentsByProvider}
                  visitSummaries={visitSummaries}
                />
              ) : (
                <div className="vai-p-6" data-testid="no-appointments-message">
                  <p className="vai-text-2xl vai-font-medium">
                    <Trans>No appointments to print</Trans>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="cancel-button"
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            data-testid="print-download-button"
            disabled={!appointments.length}
            onClick={handleClickPrint}
          >
            <Trans>Print</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
